import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, User, ArrowRight, Phone, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CustomersList = () => {
  const navigate = useNavigate();
  const [businessCustomers, setBusinessCustomers] = useState([]);
  const [privateCustomers, setPrivateCustomers] = useState([]);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      // Load business customers
      const businessResponse = await fetch(`${BACKEND_URL}/api/business-customers`);
      const businessData = await businessResponse.json();
      setBusinessCustomers(businessData);

      // Load private customers
      const privateResponse = await fetch(`${BACKEND_URL}/api/private-customers`);
      const privateData = await privateResponse.json();
      setPrivateCustomers(privateData);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-4xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold">רשימת לקוחות</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Business Customer */}
              <div 
                onClick={() => navigate('/business-customer')}
                className="group cursor-pointer"
              >
                <Card className="h-full transition-all duration-300 hover:shadow-2xl hover:scale-105 border-2 hover:border-blue-500">
                  <CardContent className="p-8 flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Building2 className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">לקוח עסקי</h3>
                    <p className="text-gray-600">ניהול לקוחות עסקיים וחברות</p>
                    <Button className="mt-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                      כניסה
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Private Customer */}
              <div 
                onClick={() => navigate('/private-customer')}
                className="group cursor-pointer"
              >
                <Card className="h-full transition-all duration-300 hover:shadow-2xl hover:scale-105 border-2 hover:border-purple-500">
                  <CardContent className="p-8 flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <User className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">לקוח פרטי</h3>
                    <p className="text-gray-600">ניהול לקוחות פרטיים</p>
                    <Button className="mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                      כניסה
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Business Customers List */}
        {businessCustomers.length > 0 && (
          <Card className="shadow-2xl border-0 mt-6">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                לקוחות עסקיים ({businessCustomers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-right font-semibold">שם חברה</th>
                      <th className="p-3 text-right font-semibold">ע.מ.</th>
                      <th className="p-3 text-right font-semibold">איש קשר</th>
                      <th className="p-3 text-right font-semibold">טלפון</th>
                      <th className="p-3 text-right font-semibold">אימייל</th>
                      <th className="p-3 text-right font-semibold">כתובת</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessCustomers.map((customer) => (
                      <tr key={customer.id} className="border-b hover:bg-blue-50">
                        <td className="p-3 font-semibold text-blue-900">{customer.companyName}</td>
                        <td className="p-3 text-gray-600">{customer.registrationNumber}</td>
                        <td className="p-3">{customer.contactPerson}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {customer.phone}
                          </div>
                        </td>
                        <td className="p-3">
                          {customer.email ? (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {customer.email}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3 text-gray-600">{customer.address || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Private Customers List */}
        {privateCustomers.length > 0 && (
          <Card className="shadow-2xl border-0 mt-6">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <User className="w-6 h-6" />
                לקוחות פרטיים ({privateCustomers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-right font-semibold">שם מלא</th>
                      <th className="p-3 text-right font-semibold">ת.ז.</th>
                      <th className="p-3 text-right font-semibold">טלפון</th>
                      <th className="p-3 text-right font-semibold">אימייל</th>
                      <th className="p-3 text-right font-semibold">כתובת</th>
                    </tr>
                  </thead>
                  <tbody>
                    {privateCustomers.map((customer) => (
                      <tr key={customer.id} className="border-b hover:bg-purple-50">
                        <td className="p-3 font-semibold text-purple-900">
                          {customer.firstName} {customer.lastName}
                        </td>
                        <td className="p-3 text-gray-600">{customer.idNumber}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {customer.phone}
                          </div>
                        </td>
                        <td className="p-3">
                          {customer.email ? (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {customer.email}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3 text-gray-600">{customer.address || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomersList;
